// src/components/forms/authForms.ts

import { createElement } from "../../utils/domHelpers";
import { getSocialLoginUrl } from "../../api/auth";
import { getSocialTempMergeQuery } from "../../services/authService";
import { Offcanvas, Toast } from "../../utils/bootstrap";
import { validateEmail } from "../../utils/helpers"; // Email validation utility
import { createLoginCodePanel } from "./loginCodePanel";

export type CreateLoginFormOptions = {
	textClass?: string;
	/** Omit when social buttons are already shown above the form. */
	hideSocialButtons?: boolean;
	redirectUrl?: string;
	isTempUser?: boolean;
	formId?: string;
	initialEmail?: string;
	onCodeSubmit?: (email: string, code: string) => Promise<void>;
};

/**
 * Creates the HTML form for user login.
 * @param onSubmit - Callback function when the form is submitted. It receives username and password.
 * @returns The created form HTMLElement.
 */
export function createLoginForm(
	onSubmit: (username: string, password: string) => Promise<void>,
	options: CreateLoginFormOptions | string = {},
): HTMLFormElement {
	const opts: CreateLoginFormOptions =
		typeof options === "string" ? { textClass: options } : options;
	const formId = opts.formId ?? "loginForm";
	const textClass = opts.textClass ?? "";
	const redirectUrl = opts.redirectUrl ?? window.location.pathname;
	const tempQuery = getSocialTempMergeQuery({ isTempUser: opts.isTempUser });
	const googleLoginUrl = `${getSocialLoginUrl(
		"google",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;
	const facebookLoginUrl = `${getSocialLoginUrl(
		"facebook",
	)}?redirect=${encodeURIComponent(redirectUrl)}${tempQuery}`;

	const socialSection = opts.hideSocialButtons
		? ""
		: `
		  <div class="gap-2 w-100">
			  <a href="${googleLoginUrl}" role="button" class="btn mt-2 btn-light w-100">
				<i class="fa-brands fa-google me-2"></i> Login with Google
			  </a>
			  <a href="${facebookLoginUrl}" role="button" class="btn mt-2 btn-light w-100">
				<i class="fa-brands fa-facebook me-2"></i> Login with Facebook
			  </a>
		  </div>
		`;

	const form = createElement("form", {
		id: formId,
		attributes: { method: "POST" },
	});

	form.innerHTML = `
		<style>
			#${formId} .form-control::placeholder { color: #6c757d !important; opacity: 1; }
			#${formId} .form-control { color: #fff; }
		</style>
		<div class="d-flex flex-column align-items-center justify-content-center">
		  <div class="mb-3 text-start w-100">
			<label for="${formId}-username" class="form-label ${textClass}">Email address</label>
			<input type="email" class="form-control" id="${formId}-username" name="username" required aria-describedby="emailHelp" />
		  </div>
		  <div class="mb-3 text-start w-100" data-password-login-fields>
			<label for="${formId}-password" class="form-label ${textClass}">Password</label>
			<input type="password" class="form-control" id="${formId}-password" name="password" required />
		  </div>
		  ${opts.onCodeSubmit ? `<div data-login-code-panel></div>` : ""}
		  <button type="submit" class="btn btn-primary w-100 mb-3">Login</button>
		  ${socialSection}
		  <p class="text-center mt-4 w-100"><small class="text-white">Forgot your password? <a href="/reset-password" class="text-info" id="${formId}-reset-password-link">Reset it here</a></small></p>
		</div>
	`;

	if (opts.onCodeSubmit) {
		const panelHost = form.querySelector<HTMLDivElement>(
			"[data-login-code-panel]",
		);
		panelHost?.replaceWith(
			createLoginCodePanel({
				getEmail: () =>
					form.querySelector<HTMLInputElement>(`#${formId}-username`)?.value ??
					"",
				onSubmit: opts.onCodeSubmit,
				onModeChange: (codeMode) => {
					form.querySelector<HTMLElement>("[data-password-login-fields]")
						?.classList.toggle("d-none", codeMode);
					const password = form.querySelector<HTMLInputElement>(`#${formId}-password`);
					const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
					if (password) password.disabled = codeMode;
					if (submit) {
						submit.classList.toggle("d-none", codeMode);
						submit.disabled = codeMode;
					}
				},
			}),
		);
	}

	if (opts.initialEmail) {
		const usernameInput = form.querySelector<HTMLInputElement>(
			`#${formId}-username`,
		);
		if (usernameInput) {
			usernameInput.value = opts.initialEmail;
		}
	}

	const resetPasswordLink = form.querySelector<HTMLAnchorElement>(
		`#${formId}-reset-password-link`,
	);
	resetPasswordLink?.addEventListener("click", (event) => {
		event.preventDefault();

		const navigateToResetPassword = (): void => {
			window.location.assign("/reset-password");
		};

		document.querySelectorAll<HTMLElement>(".toast.ace-toast").forEach((el) => {
			const toastInstance = Toast.getInstance(el) || new Toast(el);
			toastInstance.hide();
		});

		const offcanvasEl = form.closest(".offcanvas");
		if (offcanvasEl) {
			offcanvasEl.addEventListener(
				"hidden.bs.offcanvas",
				navigateToResetPassword,
				{
					once: true,
				},
			);
			const offcanvasInstance =
				Offcanvas.getInstance(offcanvasEl) || new Offcanvas(offcanvasEl);
			offcanvasInstance.hide();
			window.setTimeout(navigateToResetPassword, 500);
			return;
		}

		navigateToResetPassword();
	});

	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		const usernameInput = form.querySelector<HTMLInputElement>(
			`#${formId}-username`,
		);
		const passwordInput = form.querySelector<HTMLInputElement>(
			`#${formId}-password`,
		);
		if (passwordInput?.disabled) return;

		if (usernameInput && passwordInput && form.checkValidity()) {
			const username = usernameInput.value;
			const password = passwordInput.value;
			const submitButton = form.querySelector<HTMLButtonElement>(
				'button[type="submit"]',
			);
			if (submitButton) submitButton.disabled = true;

			try {
				await onSubmit(username, password);
			} catch (error) {
				if (submitButton) submitButton.disabled = false;
			}
		} else {
			form.reportValidity();
		}
	});

	return form;
}

/**
 * Creates the HTML form for user registration/signup.
 * @param onSubmit - Callback function when the form is submitted. It receives name, email, and password.
 * @returns The created form HTMLElement.
 */
export function createSignupForm(
	onSubmit: (name: string, email: string, password: string) => Promise<void>,
	textClass: string = "",
): HTMLFormElement {
	const form = createElement("form", {
		id: "createAccountForm",
		attributes: { method: "POST" },
	});

	form.innerHTML = `
        <style>
            #createAccountForm .form-control::placeholder { color: #6c757d !important; opacity: 1; }
            #createAccountForm .form-control { color: #fff; }
        </style>
        <div class="form-group pb-2 text-start">
          <label for="nameInput" class="form-label ${textClass}">Name</label>
          <input class="form-control" type="text" id="nameInput" name="full_name" placeholder="Your Name" required />
        </div>
        <div class="form-group pb-2 text-start">
          <label for="emailInput" class="form-label ${textClass}">Email address</label>
          <input class="form-control" type="email" id="emailInput" name="email" placeholder="Enter email" required />
        </div>
        <div class="form-group pb-2 text-start">
          <label for="passwordInput" class="form-label ${textClass}">Password</label>
          <input class="form-control" type="password" id="passwordInput" name="password" placeholder="Password (min. 8 characters)" required minlength="8" />
        </div>
        <div class="form-check pb-2 text-start" style="font-size: 14px;">
          <input class="form-check-input" type="checkbox" id="termsAndConditions" name="terms" required checked />
          <label class="form-check-label" for="termsAndConditions ${textClass}">
            I agree to <a href="/terms" class="text-info" target="_blank">Terms</a>, <a href="/privacy" class="text-info" target="_blank">Privacy Policy</a> and <a href="/competition-terms" class="text-info" target="_blank">Competition Terms</a>.
          </label>
        </div>
        <div class="mb-3">
          <button class="btn btn-primary w-100" type="submit">Create Account</button>
        </div>
        `;

	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		const nameInput = form.querySelector<HTMLInputElement>("#nameInput");
		const emailInput = form.querySelector<HTMLInputElement>("#emailInput");
		const passwordInput = form.querySelector<HTMLInputElement>("#passwordInput");
		const termsInput = form.querySelector<HTMLInputElement>(
			"#termsAndConditions",
		);

		if (
			nameInput &&
			emailInput &&
			passwordInput &&
			termsInput &&
			form.checkValidity()
		) {
			const name = nameInput.value;
			const email = emailInput.value;
			const password = passwordInput.value;

			const submitButton = form.querySelector<HTMLButtonElement>(
				'button[type="submit"]',
			);
			if (submitButton) submitButton.disabled = true;

			try {
				await onSubmit(name, email, password);
			} catch (error) {
				if (submitButton) submitButton.disabled = false;
			}
		} else {
			form.reportValidity();
		}
	});

	return form;
}

/**
 * Creates the HTML form for requesting a password reset email.
 * @param onSubmit - Callback function receiving the email address.
 * @returns The created form HTMLElement.
 */
export function createPasswordResetRequestForm(
	onSubmit: (email: string) => Promise<void>,
): HTMLFormElement {
	const form = createElement("form", {
		id: "passwordResetForm",
		attributes: { method: "POST" },
	});

	form.innerHTML = `
         <style>
             #passwordResetForm .form-control::placeholder { color: #6c757d !important; opacity: 1; }
             #passwordResetForm .form-control { color: #fff; }
         </style>
         <div class="mb-3 text-start">
           <label for="email" class="form-label text-white">Enter your account email</label>
           <input type="email" class="form-control" id="email" name="email" required placeholder="your.email@example.com" />
         </div>
         <button type="submit" class="btn btn-primary w-100">Send Reset Link</button>
     `;

	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		const emailInput = form.querySelector<HTMLInputElement>("#email");
		if (emailInput && emailInput.reportValidity()) {
			const email = emailInput.value;
			if (!validateEmail(email)) {
				emailInput.setCustomValidity("Please enter a valid email address.");
				emailInput.reportValidity();
				return;
			}
			emailInput.setCustomValidity(""); // Clear custom validity

			const submitButton = form.querySelector<HTMLButtonElement>(
				'button[type="submit"]',
			);
			if (submitButton) submitButton.disabled = true;

			try {
				await onSubmit(email);
			} catch (error) {
				if (submitButton) submitButton.disabled = false;
			}
		}
	});

	return form;
}

/**
 * Creates the HTML form for setting a new password using a token.
 * @param token - The password reset token from the URL/email link.
 * @param onSubmit - Callback function receiving the new password and confirmation.
 * @returns The created form HTMLElement.
 */
export function createPasswordResetForm(
	token: string,
	onSubmit: (password: string, passwordConfirm: string) => Promise<void>,
): HTMLFormElement {
	const form = createElement("form", {
		id: "passwordResetForm",
		attributes: { method: "POST" },
	});

	// Add hidden token input
	createElement(
		"input",
		{ attributes: { type: "hidden", name: "token", value: token } },
		form,
	);

	form.innerHTML += `
         <style>
             #passwordResetForm .form-control::placeholder { color: #6c757d !important; opacity: 1; }
             #passwordResetForm .form-control { color: #fff; }
         </style>
         <div class="mb-3 text-start">
           <label for="password" class="form-label text-white">New Password</label>
           <input type="password" class="form-control" id="password" name="password" required minlength="8" />
         </div>
         <div class="mb-3 text-start">
           <label for="passwordConfirm" class="form-label text-white">Confirm New Password</label>
           <input type="password" class="form-control" id="passwordConfirm" name="passwordConfirm" required minlength="8" />
           <div class="invalid-feedback">Passwords do not match.</div> </div>
         <button type="submit" class="btn btn-primary w-100">Set New Password</button>
     `;

	const passwordInput = form.querySelector<HTMLInputElement>("#password");
	const confirmInput = form.querySelector<HTMLInputElement>("#passwordConfirm");

	// Add validation listener
	const validateMatch = () => {
		if (passwordInput && confirmInput) {
			if (
				passwordInput.value &&
				confirmInput.value &&
				passwordInput.value !== confirmInput.value
			) {
				confirmInput.setCustomValidity("Passwords do not match.");
				confirmInput.classList.add("is-invalid"); // Show feedback
			} else {
				confirmInput.setCustomValidity("");
				confirmInput.classList.remove("is-invalid");
			}
		}
	};
	passwordInput?.addEventListener("input", validateMatch);
	confirmInput?.addEventListener("input", validateMatch);

	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		validateMatch(); // Ensure validation runs on submit

		if (passwordInput && confirmInput && form.checkValidity()) {
			const password = passwordInput.value;
			const passwordConfirm = confirmInput.value;

			const submitButton = form.querySelector<HTMLButtonElement>(
				'button[type="submit"]',
			);
			if (submitButton) submitButton.disabled = true;

			try {
				await onSubmit(password, passwordConfirm);
			} catch (error) {
				if (submitButton) submitButton.disabled = false;
			}
		} else {
			form.reportValidity();
		}
	});

	return form;
}
